import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { apiErrorMessage, launchApi } from '../api';
import { help } from '../helpTexts';
import { chipButtonSx, launchColors, overlineSx, phasePalette } from '../launchTheme';
import { CalendarWindow, YearbookState } from '../types';
import CategoryChip from '../ui/CategoryChip';
import SectionCard from '../ui/SectionCard';
import { StatusPill } from '../ui/StatusPill';
import { revisionLabel, shortDate, weekTitle } from '../yearbook/labels';

type Week = YearbookState['weeks'][number];
type Status = 'unpublished' | 'published' | 'changed';
type StatusFilter = 'all' | Status;

interface CalendarRow {
  key: string;
  chain: string;
  week: Week | null;
  window: CalendarWindow | null;
  announcement: string | null;
  statedWeek: number | null;
  revisionWeek: number | null;
  revisionYear: number | null;
  categories: string[];
  counts: Record<string, number>;
  status: Status;
}

const STATUS_PILL: Record<Exclude<Status, 'unpublished'>, { label: string; fg: string; bg: string }> = {
  published: { label: 'Publicerad', fg: launchColors.mossDark, bg: launchColors.mossLight },
  changed: { label: 'Ändrad efter publicering', fg: launchColors.warningText, bg: launchColors.warningBg },
};

const FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'Alla' },
  { value: 'unpublished', label: 'Ej publicerade' },
  { value: 'published', label: 'Publicerade' },
  { value: 'changed', label: 'Ändrade' },
];

interface Band {
  key: string;
  title: string;
  rows: CalendarRow[];
}

function bandsOf(rows: CalendarRow[]): Band[] {
  const groups: CalendarRow[][] = [];
  rows.forEach((row) => {
    const key = row.announcement ?? 'undated';
    const last = groups[groups.length - 1];
    if (last && (last[0].announcement ?? 'undated') === key) last.push(row);
    else groups.push([row]);
  });
  return groups.map((group) => {
    const when = group[0].announcement ? shortDate(group[0].announcement) : 'Utan datum';
    const weeks = Array.from(new Set(group.map((row) => row.statedWeek).filter((week): week is number => week != null)));
    return { key: group[0].announcement ?? 'undated', title: weeks.length === 1 ? `${when} · v${weeks[0]}` : when, rows: group };
  });
}

function categoryMarks(row: CalendarRow): Array<{ name: string; variant: 'published' | 'available' | 'new' }> {
  if (!row.window) return row.categories.map((name) => ({ name, variant: 'available' }));
  const published = new Set(row.window.categories ?? []);
  const names = Array.from(new Set([...(row.window.categories ?? []), ...(row.week?.categories ?? [])]));
  return names.map((name) => ({ name, variant: published.has(name) ? 'published' : 'new' }));
}

function buildRows(state: YearbookState | null, windows: CalendarWindow[]): CalendarRow[] {
  const live = windows.filter((window) => window.status !== 'superseded');
  const used = new Set<string>();
  const rows: CalendarRow[] = (state?.weeks ?? []).map((week) => {
    const window = live.find((item) => item.yearbookSheetId === week.sheetId && item.weekKey === week.weekKey) ?? null;
    if (window) used.add(window.id);
    const group = state?.groups[week.sheetId]?.find((item) => item.weekKey === week.weekKey);
    const counts = Object.fromEntries((group?.categories ?? []).map((item) => [item.category, item.rows.length]));
    return {
      key: `${week.sheetId}:${week.weekKey}`,
      chain: week.chain,
      week,
      window,
      announcement: window?.announcement ?? week.announcementDate,
      statedWeek: week.statedWeek,
      revisionWeek: window ? window.revisionWeek ?? null : week.revisionWeek,
      revisionYear: window ? window.revisionYear ?? null : week.revisionYear,
      categories: window ? window.categories ?? [] : week.categories,
      counts,
      status: !window ? 'unpublished' : window.yearbookChanged ? 'changed' : 'published',
    };
  });
  live.filter((window) => !used.has(window.id)).forEach((window) => rows.push({
    key: `window:${window.familyId}`,
    chain: window.chain,
    week: null,
    window,
    announcement: window.announcement,
    statedWeek: null,
    revisionWeek: window.revisionWeek ?? null,
    revisionYear: window.revisionYear ?? null,
    categories: window.categories ?? [],
    counts: {},
    status: 'published',
  }));
  return rows.sort((left, right) => (
    (left.announcement ?? '9999').localeCompare(right.announcement ?? '9999') || left.chain.localeCompare(right.chain, 'sv')
  ));
}

const ChainCalendarTab: React.FC = () => {
  const [state, setState] = useState<YearbookState | null>(null);
  const [windows, setWindows] = useState<CalendarWindow[]>([]);
  const [chain, setChain] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [editing, setEditing] = useState<{ row: CalendarRow | null; mode: 'edit' | 'publishChange' } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    launchApi.yearbook()
      .then(setState)
      .catch((requestError) => setError(apiErrorMessage(requestError, 'Årskalendern kunde inte hämtas.')));
    launchApi.calendarWindows()
      .then(setWindows)
      .catch((requestError) => setError(apiErrorMessage(requestError, 'Fönstren kunde inte hämtas.')));
  };

  useEffect(() => { load(); }, []);

  const rows = useMemo(() => buildRows(state, windows), [state, windows]);
  const chains = useMemo(() => Array.from(new Set([
    ...(state?.sheets ?? []).map((sheet) => sheet.chain),
    ...rows.map((row) => row.chain),
  ])), [state, rows]);
  const inChain = rows.filter((row) => !chain || row.chain === chain);
  const needle = query.trim().toLocaleLowerCase('sv');
  const visible = inChain.filter((row) => {
    if (statusFilter === 'published' && row.status === 'unpublished') return false;
    if (statusFilter !== 'all' && statusFilter !== 'published' && row.status !== statusFilter) return false;
    if (!needle) return true;
    return [row.chain, row.announcement ?? '', row.week?.announcementRaw ?? '', ...row.categories].join(' ').toLocaleLowerCase('sv').includes(needle);
  });
  const countFor = (value: StatusFilter) => inChain.filter((row) => (
    value === 'all' || (value === 'published' ? row.status !== 'unpublished' : row.status === value)
  )).length;

  const run = async (action: () => Promise<unknown>, success: string, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setEditing(null);
      setNotice(success);
      load();
    } catch (requestError) {
      setError(apiErrorMessage(requestError, failure));
    } finally {
      setBusy(false);
    }
  };

  const publishWeek = (row: CalendarRow, categories: string[]) => {
    if (!row.week) return;
    const week = row.week;
    void run(
      () => launchApi.publishYearbookWeek({ sheetId: week.sheetId, weekKey: week.weekKey, categories }),
      'Veckan är publicerad. Befintliga lanseringar behåller sitt datum.',
      'Veckan kunde inte publiceras.',
    );
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
      <SectionCard
        title="Kedjekalender"
        info={help.settingsCalendar}
        headingLevel="h3"
        padded={false}
        action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ row: null, mode: 'edit' })}>Lägg till vecka</Button>}
      >
        <Box sx={{ px: 2, borderBottom: `1px solid ${launchColors.divider}` }}>
          <Tabs value={chain} onChange={(_event, value: string) => setChain(value)} variant="scrollable" scrollButtons="auto" aria-label="Kedja">
            <Tab value="" label="Alla" sx={{ textTransform: 'none', fontWeight: 600 }} />
            {chains.map((item) => <Tab key={item} value={item} label={item} sx={{ textTransform: 'none', fontWeight: 600 }} />)}
          </Tabs>
        </Box>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ px: 2, py: 1.5 }}>
          <TextField
            size="small"
            placeholder="Sök vecka eller kategori"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Sök' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {FILTERS.map((filter) => (
              <Button key={filter.value} size="small" aria-pressed={statusFilter === filter.value} onClick={() => setStatusFilter(filter.value)} sx={chipButtonSx(statusFilter === filter.value)}>
                {`${filter.label} (${countFor(filter.value)})`}
              </Button>
            ))}
          </Stack>
        </Stack>
        <Stack spacing={2} sx={{ p: 2 }} aria-label="Veckor i kedjekalendern">
          {bandsOf(visible).map((band, index) => {
            const color = phasePalette[index % phasePalette.length];
            return (
              <Box key={`${band.key}-${index}`} sx={{ border: `1px solid ${launchColors.divider}`, borderRadius: '12px', overflow: 'hidden', borderLeft: `4px solid ${color}` }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, bgcolor: launchColors.softNavy }}>
                  <Typography component="h4" sx={{ flex: 1, fontSize: 17, fontWeight: 700, color: launchColors.navy }}>{band.title}</Typography>
                  <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>{`${band.rows.length} ${band.rows.length === 1 ? 'rad' : 'rader'}`}</Typography>
                </Stack>
                {band.rows.map((row) => {
                  const revision = revisionLabel(row);
                  const marks = categoryMarks(row);
                  return (
                    <Stack key={row.key} direction={{ xs: 'column', lg: 'row' }} spacing={1.25} alignItems={{ lg: 'center' }} sx={{ px: 2, py: 1.25, borderTop: `1px solid ${launchColors.rowDivider}`, bgcolor: launchColors.card }}>
                      {!chain && <Typography sx={{ minWidth: 120, fontWeight: 700, color: launchColors.navy }}>{row.chain}</Typography>}
                      <Box sx={{ minWidth: 168, px: 1.1, py: 0.4, borderRadius: '8px', border: `2px solid ${color}`, fontSize: 13, fontWeight: 700, color: launchColors.navy, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {revision || 'Ingen revideringsvecka'}
                      </Box>
                      {!row.week && <StatusPill label="Manuell" fg={launchColors.textSecondary} bg={launchColors.card} border={`1px solid ${launchColors.divider}`} />}
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
                        {marks.map((mark) => <CategoryChip key={mark.name} label={mark.name} variant={mark.variant} />)}
                        {marks.length === 0 && <Typography sx={{ fontSize: 13, color: launchColors.textMuted }}>Ingen vald</Typography>}
                      </Stack>
                      {row.status !== 'unpublished' && <StatusPill label={row.status === 'published' && row.window ? `Publicerad v${row.window.version}` : STATUS_PILL[row.status].label} fg={STATUS_PILL[row.status].fg} bg={STATUS_PILL[row.status].bg} />}
                      {row.window?.olderLaunchCount ? <Typography sx={{ fontSize: 12, color: launchColors.warningText, whiteSpace: 'nowrap' }}>{`${row.window.olderLaunchCount} på äldre version`}</Typography> : null}
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Button size="small" variant="outlined" onClick={() => setEditing({ row, mode: 'edit' })}>Redigera</Button>
                        {row.status === 'unpublished' && (
                          <Button size="small" disabled={busy || !row.announcement || row.categories.length === 0} onClick={() => publishWeek(row, row.categories)}>Publicera</Button>
                        )}
                        {row.status === 'changed' && (
                          <Button size="small" disabled={busy || !row.week?.announcementDate} onClick={() => setEditing({ row, mode: 'publishChange' })}>Publicera ändring</Button>
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </Box>
            );
          })}
          {visible.length === 0 && (
            <Typography sx={{ py: 3, color: launchColors.textSecondary, textAlign: 'center' }}>
              {rows.length === 0 ? 'Inga veckor ännu. Läs in årskalendern eller lägg till en vecka.' : 'Inga veckor matchar urvalet.'}
            </Typography>
          )}
        </Stack>
      </SectionCard>
      {editing && (
        <WeekDialog
          row={editing.row}
          publishChange={editing.mode === 'publishChange'}
          chains={chains}
          defaultChain={chain}
          busy={busy}
          onClose={() => setEditing(null)}
          onPublishWeek={(row, categories) => publishWeek(row, categories)}
          onRevise={(window, values) => {
            void run(
              () => launchApi.reviseCalendarWindow(window.familyId, { expectedVersion: window.version, ...values }),
              'Veckan är uppdaterad. Befintliga lanseringar behåller sitt datum.',
              'Veckan kunde inte ändras.',
            );
          }}
          onCreate={(values) => {
            void run(
              () => launchApi.publishManualWindow(values),
              'Veckan är tillagd och publicerad.',
              'Veckan kunde inte läggas till.',
            );
          }}
        />
      )}
    </Stack>
  );
};

interface RevisionValues {
  announcement: string;
  revisionWeek: number | null;
  revisionYear: number | null;
  categories: string[];
}

const numberOrNull = (value: string): number | null => (value.trim() ? Number(value) : null);
const validNumber = (value: string) => !value.trim() || Number.isInteger(Number(value));
const splitCategories = (value: string) => value.split(',').map((category) => category.trim()).filter(Boolean);

const WeekDialog: React.FC<{
  row: CalendarRow | null;
  publishChange: boolean;
  chains: string[];
  defaultChain: string;
  busy: boolean;
  onClose: () => void;
  onPublishWeek: (row: CalendarRow, categories: string[]) => void;
  onRevise: (window: CalendarWindow, values: RevisionValues) => void;
  onCreate: (values: { chain: string; announcement: string; categories: string[] }) => void;
}> = ({ row, publishChange, chains, defaultChain, busy, onClose, onPublishWeek, onRevise, onCreate }) => {
  const window = row?.window ?? null;
  const week = row?.week ?? null;
  const fromYearbook = publishChange || !window;
  const [chainName, setChainName] = useState(defaultChain);
  const [date, setDate] = useState((fromYearbook ? week?.announcementDate : window?.announcement) ?? week?.announcementDate ?? '');
  const [revisionWeek, setRevisionWeek] = useState(((fromYearbook ? week?.revisionWeek : window?.revisionWeek) ?? '')?.toString() || '');
  const [revisionYear, setRevisionYear] = useState(((fromYearbook ? week?.revisionYear : window?.revisionYear) ?? '')?.toString() || '');
  const options = Array.from(new Set([...(week?.categories ?? []), ...(window?.categories ?? [])]));
  const publishedNames = window?.categories ?? [];
  const [picked, setPicked] = useState<string[]>(publishChange
    ? Array.from(new Set([...publishedNames, ...(week?.categories ?? []).filter((category) => !publishedNames.includes(category))]))
    : window ? publishedNames : week?.categories ?? []);
  const [freeText, setFreeText] = useState((window && !week ? window.categories ?? [] : []).join(', '));
  const datesEditable = !publishChange && (!row || Boolean(window));
  const usesCheckboxes = Boolean(week);
  const categories = usesCheckboxes ? picked : splitCategories(freeText);
  const numbersValid = validNumber(revisionWeek) && validNumber(revisionYear);

  const toggle = (category: string) => setPicked((current) => (
    current.includes(category) ? current.filter((item) => item !== category) : [...current, category]
  ));

  const save = () => {
    if (publishChange && row) {
      onPublishWeek(row, categories);
    } else if (!row) {
      onCreate({ chain: chainName.trim(), announcement: date, categories });
    } else if (window) {
      onRevise(window, { announcement: date, revisionWeek: numberOrNull(revisionWeek), revisionYear: numberOrNull(revisionYear), categories });
    } else {
      onPublishWeek(row, categories);
    }
  };
  const disabled = busy || !date || !numbersValid || (!row && !chainName.trim()) || (Boolean(row) && !window && categories.length === 0);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{publishChange ? 'Publicera ändring' : row ? 'Redigera vecka' : 'Lägg till vecka'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {row && (
            <Box>
              <Typography sx={overlineSx}>{row.chain}{row.week ? '' : ' · Manuell'}</Typography>
              {week && <Typography sx={{ fontWeight: 700, color: launchColors.navy }}>{weekTitle(week)}</Typography>}
            </Box>
          )}
          {window?.olderLaunchCount ? (
            <Alert severity="warning">{`${window.olderLaunchCount} lanseringar använder en äldre version`}</Alert>
          ) : null}
          {window?.yearbookChanged && !publishChange && (
            <Alert severity="info">Årskalendern har ändrats efter publicering. Nya kategorier syns med märket Ny i listan.</Alert>
          )}
          {!row && (
            <TextField
              label="Kedja"
              value={chainName}
              onChange={(event) => setChainName(event.target.value)}
              inputProps={{ list: 'chain-calendar-chains' }}
              helperText={chains.length ? `Till exempel ${chains.slice(0, 3).join(', ')}` : undefined}
            />
          )}
          {!row && <datalist id="chain-calendar-chains">{chains.map((item) => <option key={item} value={item} />)}</datalist>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              type="date"
              label="Aviseringsdatum"
              value={date}
              disabled={!datesEditable}
              onChange={(event) => setDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={datesEditable ? undefined : 'Datumet kommer från årskalendern.'}
              sx={{ flex: 1 }}
            />
            {row && (
              <>
                <TextField
                  label="Revideringsvecka"
                  value={revisionWeek}
                  disabled={!datesEditable}
                  error={!validNumber(revisionWeek)}
                  onChange={(event) => setRevisionWeek(event.target.value)}
                  inputProps={{ inputMode: 'numeric' }}
                  sx={{ width: { sm: 150 } }}
                />
                <TextField
                  label="Revideringsår"
                  value={revisionYear}
                  disabled={!datesEditable}
                  error={!validNumber(revisionYear)}
                  onChange={(event) => setRevisionYear(event.target.value)}
                  inputProps={{ inputMode: 'numeric' }}
                  sx={{ width: { sm: 130 } }}
                />
              </>
            )}
          </Stack>
          {usesCheckboxes ? (
            <Box>
              <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ ...overlineSx, flex: 1 }}>Kategorier som kan väljas</Typography>
                <Button size="small" onClick={() => setPicked(options)}>Markera alla</Button>
                <Button size="small" onClick={() => setPicked([])}>Avmarkera alla</Button>
              </Stack>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {options.map((category) => {
                  const count = row?.counts[category];
                  const countText = count ? ` · ${count} ${count === 1 ? 'produkt' : 'produkter'}` : '';
                  return (
                    <CategoryChip
                      key={category}
                      variant="selectable"
                      selected={picked.includes(category)}
                      onToggle={() => toggle(category)}
                      label={`${category}${countText}`}
                    />
                  );
                })}
                {options.length === 0 && <Typography sx={{ color: launchColors.textSecondary }}>Veckan har inga kategorier.</Typography>}
              </Stack>
            </Box>
          ) : (
            <TextField label="Kategorier" helperText="Avgränsa med komma." value={freeText} onChange={(event) => setFreeText(event.target.value)} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" disabled={disabled} onClick={save}>
          {publishChange ? 'Publicera ändring' : !row ? 'Lägg till och publicera' : window ? 'Spara ny version' : 'Publicera'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChainCalendarTab;
